import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PackageCheck, Warehouse, Boxes, IndianRupee } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { KpiCard } from '@/components/ui/KpiCard';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { apiError } from '@/lib/apiError';
import { formatDate, formatINR } from '@/lib/format';
import { listSisReturnPool, receiveSisReturnPool, type SisReturnPoolRow } from '@/api/sisReturnPool';

export function SisReturnPoolPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const [status, setStatus] = useState('Open');

  const filter = useMemo(() => ({ status }), [status]);
  const query = useQuery({ queryKey: ['sis-return-pool', filter], queryFn: () => listSisReturnPool(filter) });
  const rows = query.data?.data ?? [];
  const summary = query.data?.summary;

  const receiveMut = useMutation({
    mutationFn: (id: string) => receiveSisReturnPool(id),
    onSuccess: (r) => {
      toast.push(`Received ${r.receivedQty} unit(s) into warehouse`, 'success');
      qc.invalidateQueries({ queryKey: ['sis-return-pool'] });
      qc.invalidateQueries({ queryKey: ['warehouse-stock'] });
      qc.invalidateQueries({ queryKey: ['wh-ledger'] });
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Receive failed'), 'error'),
  });

  async function onReceive(r: SisReturnPoolRow) {
    if (!await confirmDlg(`Receive ${r.qty} × ${r.itemName || r.itemCode} into GRHAMOY warehouse stock?`)) return;
    receiveMut.mutate(r.id);
  }

  return (
    <div>
      <SectionHeader
        title="SIS Returns"      />

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiCard label="Open Lines" value={summary?.openLines ?? 0} accent="#f59e0b" Icon={PackageCheck} loading={query.isFetching && !summary} />
        <KpiCard label="Open Units" value={summary?.openQty ?? 0} accent="#6366f1" Icon={Boxes} loading={query.isFetching && !summary} />
        <KpiCard label="Open Value" value={`₹${formatINR(summary?.openValue ?? 0)}`} accent="#10b981" Icon={IndianRupee} loading={query.isFetching && !summary} />
      </div>

      <FilterBar>
        <FilterField label="Status" className="sm:w-[160px]">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="Open">Open</option>
            <option value="Received">Received</option>
            <option value="All">All</option>
          </Select>
        </FilterField>
      </FilterBar>

      <Table<SisReturnPoolRow>
        paginated
        storageKey="sis-return-pool"
        title="Returned from SIS"
        count={`${rows.length}`}
        rows={rows}
        rowKey={(r) => r.id}
        loading={query.isFetching && rows.length === 0}
        columns={[
          { key: 'store', header: 'Store', cell: (r) => <strong>{r.storeName}</strong> },
          { key: 'item', header: 'Item', cell: (r) => `${r.itemCode}${r.itemName ? ` · ${r.itemName}` : ''}` },
          { key: 'barcode', header: 'Barcode', cell: (r) => r.itemBarcode || '—' },
          { key: 'qty', header: 'Qty', align: 'right', cell: (r) => <strong>{r.qty}</strong> },
          { key: 'value', header: 'Value', align: 'right', cell: (r) => `₹${formatINR(r.value)}` },
          { key: 'invoice', header: 'Dispatch Inv', cell: (r) => r.invoiceNo || '—' },
          { key: 'returned', header: 'Returned', align: 'center', cell: (r) => formatDate(r.returnedAt) },
          {
            key: 'status',
            header: 'Status',
            align: 'center',
            cell: (r) =>
              r.status === 'Received' ? (
                <Badge tone="success">Received{r.receivedAt ? ` · ${formatDate(r.receivedAt)}` : ''}</Badge>
              ) : (
                <Badge tone="warning">Open</Badge>
              ),
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            cell: (r) =>
              r.status === 'Open' ? (
                <Button variant="primary" size="sm" icon={<Warehouse size={13} />} disabled={receiveMut.isPending} onClick={() => onReceive(r)}>
                  Receive
                </Button>
              ) : (
                <span className="text-[11px] text-[var(--text-muted)]">{r.receivedBy ?? '—'}</span>
              ),
          },
        ]}
      />
    </div>
  );
}
